import React, { ReactElement, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import styled from 'styled-components/macro';
import { useForm } from 'react-hook-form';
import {
  EmergencyClinicsFragment,
  useCreateEmergencyClinicMutation,
  useUpdateEmergencyClinicMutation,
} from 'shared/types/graphql';
import { phone as phoneNumberRegex } from 'shared/utils/validation';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  CModal,
  ModalOverlay,
  ModalContent,
  CModalHeader,
  CModalFooter,
  CModalBody,
  ModalCloseButton,
  Button,
  useToast,
} from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsClincHoursEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IEmergencyContactForm {
  name: string;
  phone: string;
  addressLine1: string | null;
  addressLine2: string;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  displayOrder: number;
}

interface IEditEmergencyContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  clinic: EmergencyClinicsFragment | undefined;
}

const EditEmergencyContactModal = ({ isOpen, onClose, clinic }: IEditEmergencyContactModalProps): ReactElement => {
  const { currentClinic } = useClinicUser();
  const clinicSettingId = currentClinic?.clinicSetting?.id;
  const { trackEvent } = useTracking();

  const toast = useToast();
  const [createEmergencyClinic, { loading: isCreating }] = useCreateEmergencyClinicMutation();
  const [updateEmergencyClinic, { loading: isUpdating }] = useUpdateEmergencyClinicMutation();

  const isAddingEmergencyContact = !clinic;
  const isSavingEmergencyContact = isUpdating || isCreating;

  const { handleSubmit, register, reset, getValues, setValue, errors } = useForm<IEmergencyContactForm>({
    mode: 'onBlur',
    validateCriteriaMode: 'all',
  });

  useEffect(() => {
    if (!!clinic) {
      const { name, phone, addressLine1, addressLine2, city, state, postalCode } = clinic;
      reset({
        name,
        addressLine1,
        addressLine2: addressLine2 || '',
        city,
        state,
        postalCode,
        phone,
      });
    } else {
      reset({
        name: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        phone: '',
      });
    }
  }, [clinic, reset]);

  const handleCancelClick = (): void => {
    onClose();
  };

  const handleSaveEmergencyContact = async (data: IEmergencyContactForm): Promise<void> => {
    if (isSavingEmergencyContact) return;

    try {
      if (isAddingEmergencyContact) {
        await createEmergencyClinic({
          variables: {
            data: {
              clinicSetting: { connect: { id: clinicSettingId } },
              ...data,
              displayOrder: 1,
            },
          },
        });
      } else {
        await updateEmergencyClinic({
          variables: {
            where: { id: clinic?.id },
            data: data,
          },
        });
      }
      toast({
        status: 'success',
        title: `Contact successfully ${isAddingEmergencyContact ? 'added' : 'saved'}`,
      });

      if (!onClose) return;
      onClose();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      toast({
        status: 'error',
        title: `There was a problem ${isAddingEmergencyContact ? 'adding' : 'saving'} the emergency contact`,
      });
    }
  };

  const handlePhoneNumberBlur = (): void => {
    const phone = getValues('phone');
    if (!!phone) {
      setValue('phone', phone.replace(phoneNumberRegex, '($2) $3-$4'));
    }

    trackEvent(SettingsClincHoursEvents.EditEmergencyContactPhone);
  };

  return (
    <CModal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <StyledForm onSubmit={handleSubmit(handleSaveEmergencyContact)}>
          <CModalHeader>Emergency Contact Information</CModalHeader>
          <ModalCloseButton />
          <ModalDescription>Enter your preferred emergency clinic&apos;s information below:</ModalDescription>
          <CModalBody>
            <Row>
              <Label flex="2">
                <LabelText required>Emergency Clinic Name</LabelText>
                <Input
                  name="name"
                  ref={register({
                    required: 'Please provide a clinic name',
                  })}
                  className={errors.name ? 'invalid' : ''}
                  onBlur={(): void => {
                    trackEvent(SettingsClincHoursEvents.EditEmergencyClinicName);
                  }}
                ></Input>
                {errors.name && <ValidationMessage>{errors.name?.message}</ValidationMessage>}
              </Label>
              <Label flex="2">
                <LabelText required>Phone Number</LabelText>
                <Input
                  name="phone"
                  ref={register({
                    pattern: {
                      value: phoneNumberRegex,
                      message: 'Please provide a valid US phone number',
                    },
                    required: 'Please provide a phone number',
                  })}
                  className={errors.phone ? 'invalid' : ''}
                  onBlur={handlePhoneNumberBlur}
                ></Input>
                {errors.phone && <ValidationMessage>{errors.phone?.message}</ValidationMessage>}
              </Label>
            </Row>
            <Row>
              <Label flex="3">
                <LabelText required>Street Address 1</LabelText>
                <Input
                  name="addressLine1"
                  ref={register({ required: 'Please provide a street address' })}
                  placeholder="1400 Mulberry Lane"
                  className={errors.addressLine1 ? 'invalid' : ''}
                  onBlur={(): void => {
                    trackEvent(SettingsClincHoursEvents.EditEmergencyClinicAddress);
                  }}
                ></Input>
                {errors.addressLine1 && <ValidationMessage>{errors.addressLine1?.message}</ValidationMessage>}
              </Label>
              <Label flex="3">
                <LabelText>Street Address 2</LabelText>
                <Input
                  name="addressLine2"
                  ref={register()}
                  placeholder="Suite 1738 #5"
                  onBlur={(): void => {
                    trackEvent(SettingsClincHoursEvents.EditEmergencyClinicAddress);
                  }}
                ></Input>
              </Label>
            </Row>
            <Row>
              <Label flex="3">
                <LabelText required>City</LabelText>
                <Input
                  name="city"
                  ref={register({ required: 'Please provide a city' })}
                  placeholder="Austin"
                  className={errors.city ? 'invalid' : ''}
                  onBlur={(): void => {
                    trackEvent(SettingsClincHoursEvents.EditEmergencyClinicCity);
                  }}
                ></Input>
                {errors.city && <ValidationMessage>{errors.city?.message}</ValidationMessage>}
              </Label>
              <Label flex="3">
                <LabelText required>State</LabelText>
                <Input
                  name="state"
                  ref={register({ required: 'Please provide a state' })}
                  placeholder="TX"
                  maxLength={3}
                  className={errors.state ? 'invalid' : ''}
                  onBlur={(): void => {
                    trackEvent(SettingsClincHoursEvents.EditEmergencyClinicState);
                  }}
                ></Input>
                {errors.state && <ValidationMessage>{errors.state?.message}</ValidationMessage>}
              </Label>
            </Row>
            <Row>
              <Label flex="3">
                <LabelText required>Zip Code</LabelText>
                <Input
                  name="postalCode"
                  ref={register({ required: 'Please provide a zip code' })}
                  className={errors.postalCode ? 'invalid' : ''}
                  placeholder="12345"
                  maxLength={6}
                  onBlur={(): void => {
                    trackEvent(SettingsClincHoursEvents.EditEmergenctClinicZip);
                  }}
                ></Input>
                {errors.postalCode && <ValidationMessage>{errors.postalCode?.message}</ValidationMessage>}
              </Label>
              <Label flex="3" />
            </Row>
          </CModalBody>
          <CModalFooter>
            <Button variant="tertiary" onClick={handleCancelClick} mr={3}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSavingEmergencyContact}
              isLoading={isSavingEmergencyContact}
              onClick={(): void => {
                trackEvent(SettingsClincHoursEvents.SubmitEmergencyClinicInfo);
              }}
            >
              Save
            </Button>
          </CModalFooter>
        </StyledForm>
      </ModalContent>
    </CModal>
  );
};

const ModalDescription = styled.p`
  padding: 0 24px;
`;

const StyledForm = styled.form`
  padding: 20px;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin: 25px 0;
`;

interface ILabelProps {
  flex?: string;
}

const Input = styled.input``;

const Label = styled.label<ILabelProps>`
  flex: ${({ flex }): string => (flex ? flex : '1 1 auto')};
  display: flex;
  flex-direction: column;
  margin: 0 15px;
  font-size: 14px;

  &:first-of-type {
    margin-left: 0;
  }

  &:last-of-type {
    margin-right: 0;
  }

  input,
  select {
    height: 38px;
    width: 100%;
    border-radius: 7px;
    border: 1px solid hsl(0, 0%, 82%);
    padding: 9px 12px;
    font-size: 14px;
    background-color: var(--chakra-colors-background-default);
    color: hsl(235, 81%, 13%);
    transition: border-color 0.1s ease-in-out;

    &:hover:not([disabled]) {
      border-color: hsl(0, 0%, 70%);
    }

    &.invalid:not([disabled]) {
      border-color: #da4167;
    }

    &:focus:not([disabled]) {
      border-color: var(--chakra-colors-text-brand);
    }
  }

  input[disabled],
  select[disabled] {
    background: #f3f3f3;
    opacity: 0.8;
  }

  select {
    appearance: none;
    background: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjUiIHZpZXdCb3g9IjAgMCA4IDUiPgogICAgPGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4KICAgICAgICA8ZyBmaWxsPSIjMDUwRjVGIiBmaWxsLXJ1bGU9Im5vbnplcm8iPgogICAgICAgICAgICA8cGF0aCBkPSJNMTgwMyAyMjQuMDc1bC0yLjcxNC0yLjg0NGMtLjI5NC0uMzA4LS43NzEtLjMwOC0xLjA2NSAwLS4yOTUuMzA4LS4yOTUuODA4IDAgMS4xMTdsMy4yNzcgMy40MzRjLjI3OC4yOS43MjcuMjkgMS4wMDQgMGwzLjI3Ny0zLjQzNGMuMjk1LS4zMDkuMjk1LS44MDkgMC0xLjExNy0uMjk0LS4zMDgtLjc3LS4zMDgtMS4wNjUgMGwtMi43MTQgMi44NDR6IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMTc5OSAtMjIxKSIvPgogICAgICAgIDwvZz4KICAgIDwvZz4KPC9zdmc+Cg==)
      no-repeat calc(100% - 10px) 50%;
    background-color: var(--chakra-colors-background-default);
    padding-right: 28px;

    &:hover {
      cursor: pointer;
    }
  }
`;

interface ILabelTextProps {
  required?: boolean;
}

const LabelText = styled.div<ILabelTextProps>`
  margin-bottom: 10px;

  font-weight: 600;

  &[required]:after {
    content: '*';
    color: var(--chakra-colors-text-danger);
    margin-left: 2px;
  }
`;

const ValidationMessage = styled.div`
  margin-top: 8px;
  color: var(--chakra-colors-text-danger);
`;

export default EditEmergencyContactModal;
